import { OpenInFull, PostAdd } from "@mui/icons-material";
import { Popover } from "@mui/material";
import clsx from "clsx";
import { FC, MouseEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import { FramerTooltip } from "@ryde/controls";
import { selectedTableRowsIdsAtomFamily, useTimezone } from "@ryde/shared";

import { cellKey } from "../rides-calendar.helpers";
import {
  RIDES_CALENDAR_CELL_VISIBLE_CHIP_COUNT,
  RIDES_CALENDAR_ENTITY,
} from "./rides-calendar.constants";
import { IRidesCalendarBodyCellProps } from "./rides-calendar.models";
import { RidesCalendarRideChip } from "./rides-calendar.ride-chip";

export const RidesCalendarBodyCell: FC<IRidesCalendarBodyCellProps> = ({
  style,
  dateKey,
  hourIndex,
  eventMap,
  onOpenRide,
  onAddRide,
}) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const events = useMemo(
    () => eventMap.get(cellKey(dateKey, hourIndex)) ?? [],
    [eventMap, dateKey, hourIndex],
  );
  const visibleEvents = events.slice(0, RIDES_CALENDAR_CELL_VISIBLE_CHIP_COUNT);
  const overflowCount = events.length - visibleEvents.length;
  const hasOverflow = overflowCount > 0;

  const [selectedIds, setSelectedIds] = useRecoilState(
    selectedTableRowsIdsAtomFamily(RIDES_CALENDAR_ENTITY),
  );

  const cellEventIds = useMemo(() => events.map(e => e.id), [events]);

  const { selectedSet, selectedInCellCount, isAllInCellSelected } =
    useMemo(() => {
      const set = new Set(selectedIds);
      const inCell = cellEventIds.filter(id => set.has(id)).length;
      return {
        selectedSet: set,
        selectedInCellCount: inCell,
        isAllInCellSelected:
          cellEventIds.length > 0 && inCell === cellEventIds.length,
      };
    }, [selectedIds, cellEventIds]);

  const hasSelection = selectedInCellCount > 0;
  const hasEvents = events.length > 0;

  const [expandAnchor, setExpandAnchor] = useState<HTMLButtonElement | null>(
    null,
  );
  const isExpandOpen = Boolean(expandAnchor);

  const toggleRideSelected = (rideId: number): void => {
    setSelectedIds(prev =>
      prev.includes(rideId)
        ? prev.filter(id => id !== rideId)
        : [...prev, rideId],
    );
  };

  const toggleSelectAllInCell = (): void => {
    if (!cellEventIds.length) {
      return;
    }
    setSelectedIds(prev => {
      if (isAllInCellSelected) {
        return prev.filter(id => !cellEventIds.includes(id));
      }
      const merged = new Set([...prev, ...cellEventIds]);
      return Array.from(merged);
    });
  };

  const handleAddClick = (e: MouseEvent): void => {
    e.stopPropagation();
    onAddRide(dateKey, hourIndex);
  };

  const handleExpandClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    setExpandAnchor(prev => (prev ? null : e.currentTarget));
  };

  const handleCloseExpand = (): void => setExpandAnchor(null);

  const handleOpenRideFromPopper = (rideId: number): void => {
    setExpandAnchor(null);
    onOpenRide(rideId);
  };

  return (
    <div
      className={clsx(
        "rc-body-cell",
        hasSelection && "rc-body-cell--selected",
        hasOverflow && "rc-body-cell--has-overflow",
        isExpandOpen && "rc-body-cell--expand-open",
      )}
      style={style}
      data-e2e={`rides-calendar-cell-${dateKey}-${hourIndex}`}
    >
      <div className="rc-body-cell__list thin_scrollbar">
        {visibleEvents.map(event => (
          <RidesCalendarRideChip
            key={event.id}
            event={event}
            timezone={timezone}
            isSelected={selectedSet.has(event.id)}
            onToggleSelected={toggleRideSelected}
            onOpenRide={onOpenRide}
          />
        ))}
      </div>

      <div
        className={clsx(
          "rc-body-cell__footer",
          (hasSelection || hasEvents || hasOverflow) &&
            "rc-body-cell__footer--pinned",
        )}
      >
        <div className="rc-body-cell__footer-start">
          {hasEvents && (
            <button
              type="button"
              className={clsx(
                "rc-body-cell__select-link",
                isAllInCellSelected && "rc-body-cell__select-link--deselect",
              )}
              onClick={toggleSelectAllInCell}
              data-e2e={`rides-calendar-select-all-${dateKey}-${hourIndex}`}
            >
              {isAllInCellSelected
                ? t("ride.calendar.deselectAll", { defaultValue: "Deselect" })
                : t("ride.calendar.selectAll", { defaultValue: "Select" })}
            </button>
          )}
        </div>
        <div className="rc-body-cell__footer-end">
          {hasEvents && hasSelection && (
            <span className="rc-body-cell__badge rc-body-cell__badge--selected">
              {selectedInCellCount}/{events.length}
            </span>
          )}
          {hasEvents && !hasSelection && (
            <span className="rc-body-cell__badge">{events.length}</span>
          )}
          {hasOverflow && (
            <FramerTooltip
              content={t("course.calendar.expandCellTooltip", {
                defaultValue: "Expand cell",
              })}
              mouseEnterDelay={500}
            >
              <button
                type="button"
                className="rc-body-cell__expand"
                onClick={handleExpandClick}
                data-e2e={`rides-calendar-expand-${dateKey}-${hourIndex}`}
                aria-label="expand cell"
                aria-expanded={isExpandOpen}
              >
                <OpenInFull sx={{ fontSize: 14, color: "inherit" }} />
              </button>
            </FramerTooltip>
          )}
          <FramerTooltip
            content={t("ride.calendar.addRide", { defaultValue: "Add ride" })}
            mouseEnterDelay={500}
          >
            <button
              type="button"
              className="rc-body-cell__add"
              onClick={handleAddClick}
              data-e2e={`rides-calendar-add-${dateKey}-${hourIndex}`}
              aria-label="add ride"
            >
              <PostAdd className="rc-body-cell__add-icon" fontSize="small" />
            </button>
          </FramerTooltip>
        </div>
      </div>

      <Popover
        open={isExpandOpen}
        anchorEl={expandAnchor}
        onClose={handleCloseExpand}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { className: "rc-popover-paper" } }}
      >
        <div className="rc-popover">
          <div className="rc-popover__header">
            {t("ride.calendar.allRidesAtSlot", {
              defaultValue: "Rides at this slot",
            })}
            <span className="rc-popover__header-count">{events.length}</span>
          </div>
          <div className="rc-popover__list thin_scrollbar">
            {events.map(event => (
              <RidesCalendarRideChip
                key={event.id}
                event={event}
                timezone={timezone}
                isSelected={selectedSet.has(event.id)}
                onToggleSelected={toggleRideSelected}
                onOpenRide={handleOpenRideFromPopper}
              />
            ))}
          </div>
        </div>
      </Popover>
    </div>
  );
};
